"use client";

import { useMemo, useState } from "react";
import { ArrowRight, Braces, GitCompareArrows, Settings2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type { TextDiffSegment, ViewChangeEntry } from "@/types/view-changes";
import type { ViewChangesIdNameMaps } from "@/types/view-changes";
import {
  buildChangedVariablesJson,
  buildInlineTextDiff,
  buildUnifiedJsonDiff,
  formatFieldValue,
  getChangedEntries,
  resolveEntriesWithNames,
  toPrettyJson,
} from "@/utils/view-changes";

interface ViewChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  before?: unknown;
  after?: unknown;
  fieldLabels?: Record<string, string>;
  idNameMaps?: ViewChangesIdNameMaps;
}

function InlineDiffText({
  segments,
  className,
}: {
  segments: TextDiffSegment[];
  className?: string;
}) {
  return (
    <span
      className={cn(
        "whitespace-pre-wrap break-words text-[13px] leading-6 text-foreground/90",
        className
      )}
    >
      {segments.map((segment, index) => {
        if (segment.kind === "same") {
          return <span key={index}>{segment.text}</span>;
        }
        if (segment.kind === "added") {
          return (
            <mark
              key={index}
              className="rounded-[4px] bg-emerald-400/45 px-0.5 font-medium text-emerald-950 dark:bg-emerald-400/35 dark:text-emerald-50"
            >
              {segment.text}
            </mark>
          );
        }
        return (
          <mark
            key={index}
            className="rounded-[4px] bg-rose-400/35 px-0.5 text-rose-950 line-through decoration-rose-600/80 dark:bg-rose-400/30 dark:text-rose-50"
          >
            {segment.text}
          </mark>
        );
      })}
    </span>
  );
}

function isTextLike(value: unknown): boolean {
  return typeof value === "string" || value === null || value === undefined;
}

function changeKind(entry: ViewChangeEntry): "added" | "removed" | "modified" {
  const beforeEmpty =
    entry.before === null ||
    entry.before === undefined ||
    entry.before === "";
  const afterEmpty =
    entry.after === null || entry.after === undefined || entry.after === "";
  if (beforeEmpty && !afterEmpty) return "added";
  if (!beforeEmpty && afterEmpty) return "removed";
  return "modified";
}

function ChangedFieldCard({ entry }: { entry: ViewChangeEntry }) {
  const beforeText = formatFieldValue(entry.before);
  const afterText = formatFieldValue(entry.after);
  const kind = changeKind(entry);
  const useInline =
    isTextLike(entry.before) &&
    isTextLike(entry.after) &&
    typeof entry.before !== "boolean" &&
    typeof entry.after !== "boolean";

  const beforeRaw =
    typeof entry.before === "string"
      ? entry.before
      : beforeText === "—"
        ? ""
        : beforeText;
  const afterRaw =
    typeof entry.after === "string"
      ? entry.after
      : afterText === "—"
        ? ""
        : afterText;

  const segments = useInline
    ? buildInlineTextDiff(beforeRaw, afterRaw)
    : null;

  const isLongText =
    Boolean(segments) &&
    (beforeRaw.includes("\n") ||
      afterRaw.includes("\n") ||
      beforeRaw.length > 80 ||
      afterRaw.length > 80);

  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border bg-card shadow-sm",
        kind === "added" && "border-emerald-200/80 dark:border-emerald-900/50",
        kind === "removed" && "border-rose-200/80 dark:border-rose-900/50",
        kind === "modified" && "border-border"
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-border/70 bg-muted/20 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className={cn(
              "h-8 w-1 shrink-0 rounded-full",
              kind === "added" && "bg-emerald-500",
              kind === "removed" && "bg-rose-500",
              kind === "modified" && "bg-primary"
            )}
          />
          <h3 className="truncate text-sm font-semibold tracking-tight text-foreground">
            {entry.label}
          </h3>
        </div>
        <Badge
          variant="outline"
          className={cn(
            "shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            kind === "added" &&
              "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
            kind === "removed" &&
              "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300",
            kind === "modified" &&
              "border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300"
          )}
        >
          {kind}
        </Badge>
      </div>

      <div className="p-4">
        {segments ? (
          <div
            className={cn(
              "rounded-xl border border-border/60 bg-gradient-to-b from-muted/50 to-muted/20 px-4 py-3.5 dark:from-muted/30 dark:to-muted/10",
              isLongText && "max-h-72 overflow-y-auto"
            )}
          >
            <InlineDiffText segments={segments} />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2.5 text-sm">
            <span className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-rose-800 line-through dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
              {beforeText}
            </span>
            <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 font-medium text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
              {afterText}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

export default function ViewChangesDialog({
  open,
  onOpenChange,
  before,
  after,
  fieldLabels,
  idNameMaps,
}: ViewChangesDialogProps) {
  const [tab, setTab] = useState("settings");

  const entries = useMemo(
    () =>
      resolveEntriesWithNames(
        getChangedEntries(before, after, fieldLabels),
        idNameMaps
      ),
    [before, after, fieldLabels, idNameMaps]
  );

  const jsonLines = useMemo(() => {
    if (entries.length === 0) {
      return [{ kind: "same" as const, text: "{}" }];
    }
    const { before: changedBefore, after: changedAfter } =
      buildChangedVariablesJson(entries);
    return buildUnifiedJsonDiff(
      toPrettyJson(changedBefore),
      toPrettyJson(changedAfter),
      1
    );
  }, [entries]);

  const changeCount = entries.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-3xl flex-col gap-0 overflow-hidden border-border/80 p-0 shadow-2xl sm:rounded-2xl">
        <DialogHeader className="relative shrink-0 overflow-hidden border-b border-border px-6 pb-4 pt-5 text-left">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-transparent dark:from-violet-500/15" />
          <div className="relative flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-100 shadow-sm dark:bg-violet-900/40">
              <GitCompareArrows className="size-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <DialogTitle className="text-lg font-semibold tracking-tight">
                  View changes
                </DialogTitle>
                {changeCount > 0 && (
                  <Badge className="rounded-full bg-primary/10 px-2.5 text-primary hover:bg-primary/10">
                    {changeCount} {changeCount === 1 ? "field" : "fields"}
                  </Badge>
                )}
              </div>
              <DialogDescription className="text-sm">
                Comparing current edits with the saved version
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={setTab}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="shrink-0 border-b border-border px-6 py-3">
            <TabsList className="h-10 w-full justify-start gap-1 rounded-xl bg-muted/60 p-1 dark:bg-muted/40">
              <TabsTrigger
                value="settings"
                className="gap-2 rounded-lg px-3.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Settings2 className="size-3.5" />
                Settings
              </TabsTrigger>
              <TabsTrigger
                value="json"
                className="gap-2 rounded-lg px-3.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Braces className="size-3.5" />
                JSON
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent
            value="settings"
            className="mt-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
          >
            <ScrollArea className="h-[min(58vh,520px)]">
              <div className="space-y-3.5 p-5 sm:p-6">
                {entries.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border py-16 text-center">
                    <GitCompareArrows className="size-8 text-muted-foreground/50" />
                    <p className="text-sm font-medium text-foreground">
                      No differences
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Everything matches the saved version
                    </p>
                  </div>
                ) : (
                  entries.map((entry) => (
                    <ChangedFieldCard key={entry.key} entry={entry} />
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent
            value="json"
            className="mt-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
          >
            <ScrollArea className="h-[min(58vh,520px)]">
              <div className="p-4 sm:p-5">
                <div className="overflow-hidden rounded-2xl border border-border bg-muted/30 dark:bg-muted/20">
                  <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                    <Braces className="size-3.5 text-muted-foreground" />
                    <span className="text-[11px] font-medium text-muted-foreground">
                      changed-fields.json
                    </span>
                  </div>
                  <div className="font-mono text-[12px] leading-5 text-foreground">
                    {jsonLines.map((line, index) => {
                      if (line.kind === "context-gap") {
                        return (
                          <div
                            key={`gap-${index}`}
                            className="px-4 py-2 text-center text-muted-foreground"
                          >
                            ···
                          </div>
                        );
                      }

                      return (
                        <div
                          key={`${line.kind}-${index}`}
                          className={cn(
                            "flex gap-3 px-4 py-0.5",
                            line.kind === "added" && "bg-emerald-500/15",
                            line.kind === "removed" && "bg-rose-500/15"
                          )}
                        >
                          <span className="w-7 shrink-0 select-none text-right text-muted-foreground">
                            {line.beforeLine ?? ""}
                          </span>
                          <span className="w-7 shrink-0 select-none text-right text-muted-foreground">
                            {line.afterLine ?? ""}
                          </span>
                          <span
                            className={cn(
                              "w-3 shrink-0 select-none",
                              line.kind === "added" &&
                                "text-emerald-600 dark:text-emerald-400",
                              line.kind === "removed" &&
                                "text-rose-600 dark:text-rose-400",
                              line.kind === "same" && "text-muted-foreground/40"
                            )}
                          >
                            {line.kind === "added"
                              ? "+"
                              : line.kind === "removed"
                                ? "-"
                                : " "}
                          </span>
                          <pre className="min-w-0 flex-1 whitespace-pre-wrap break-all">
                            {line.segments ? (
                              <InlineDiffText
                                segments={line.segments}
                                className="text-[12px] leading-5"
                              />
                            ) : (
                              line.text
                            )}
                          </pre>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
